if (!customElements.get('locale-selects')) {
  class LocaleSelects extends HTMLElement {
    constructor() {
      super();
      this.countrySelect = this.querySelector('select[name="country"]');
      this.currencySelect = this.querySelector('select[name="currency"]');
      this.languageSelect = this.querySelector('select[name="language"]');
      this.applyButton = this.querySelector('[data-apply-locale]');
      this.flagContainer = this.querySelector('[data-country-flag]');

      this.current = {
        country: this.dataset.currentCountry || '',
        currency: this.dataset.currentCurrency || '',
        language: this.dataset.currentLanguage || ''
      };

      this.localeData = this.parseLocaleData();

      this.onCountryChange = this.onCountryChange.bind(this);
      this.onApply = this.onApply.bind(this);
    }

    connectedCallback() {
      if (!this.countrySelect || !this.currencySelect || !this.languageSelect) return;

      // Populate countries from data if country select is empty
      if (this.countrySelect.options.length === 0 && this.localeData.length) {
        this.populateCountries();
      }

      // Initialize dependent selects
      const initialCountry = this.countrySelect.value || this.current.country;
      this.updateCurrencyAndLanguage(initialCountry);
      this.renderCountryFlag(initialCountry);

      // Event listeners
      this.countrySelect.addEventListener('change', this.onCountryChange);
      if (this.applyButton) this.applyButton.addEventListener('click', this.onApply);
    }

    disconnectedCallback() {
      if (this.countrySelect) this.countrySelect.removeEventListener('change', this.onCountryChange);
      if (this.applyButton) this.applyButton.removeEventListener('click', this.onApply);
    }

    parseLocaleData() {
      try {
        const script = this.querySelector('script.locale-data[type="application/json"]');
        if (!script) return [];
        const data = JSON.parse(script.textContent || '[]');
        if (!Array.isArray(data)) return [];
        return data.map((entry) => ({
          name: String(entry.name || ''),
          code: String(entry.code || ''),
          storeUrl: String(entry.store_url || ''),
          marketLocale: String(entry.locale || ''),
          currencies: Array.isArray(entry.currencies) ? entry.currencies.map((c) => String(c)) : [],
          languages: Array.isArray(entry.languages)
            ? entry.languages.map((l) =>
                typeof l === 'string'
                  ? { code: l, label: l }
                  : { code: String(l.code || l.value || ''), label: String(l.label || l.title || l.code || l.value || '') }
              )
            : []
        }));
      } catch (e) {
        console.warn('Failed to parse locale data', e);
        return [];
      }
    }

    buildTargetUrl() {
      const selectedCountry = this.countrySelect.value;
      const selectedLanguage = this.languageSelect.value;
      const localeEntry = this.findLocaleByCountry(selectedCountry);

      if (!localeEntry) return null;

      const base = (localeEntry.storeUrl || window.location.origin).replace(/\/$/, '');
      const lang = String(selectedLanguage || '').toLowerCase();
      const market = String(localeEntry.marketLocale || localeEntry.code || '').toLowerCase();
      // If language is 'de', do not include a language segment – use base store URL only
      if (
        (lang === 'de' && market === 'de') ||
        (lang === 'en' && market === 'us') ||
        (lang === 'en' && market === 'gb') ||
        (lang === 'en' && market === 'ca')
      )
        return base;
      if (!lang || !market) return null;
      // If market/locale is 'de', only include the language code (e.g., /en)
      if (market === 'de') return `${base}/${lang}`;
      // Default behavior: /{lang}-{market}
      return `${base}/${lang}-${market}`;
    }

    populateCountries() {
      const fragment = document.createDocumentFragment();
      this.localeData.forEach((loc) => {
        const option = document.createElement('option');
        option.value = loc.name || loc.code || '';
        option.textContent = loc.name || loc.code || '';
        if (this.current.country && (this.current.country === loc.name || this.current.country === loc.code)) {
          option.selected = true;
        }
        fragment.appendChild(option);
      });
      this.countrySelect.appendChild(fragment);
    }

    findLocaleByCountry(countryValue) {
      if (!countryValue) return null;
      const valueLower = String(countryValue).toLowerCase();
      return (
        this.localeData.find((l) => l.name.toLowerCase() === valueLower) ||
        this.localeData.find((l) => (l.code || '').toLowerCase() === valueLower) ||
        null
      );
    }

    updateCurrencyAndLanguage(countryValue) {
      const loc = this.findLocaleByCountry(countryValue);

      // Currencies
      this.currencySelect.innerHTML = '';
      const currencies = (loc && Array.isArray(loc.currencies) ? loc.currencies : []).map((c) => String(c));
      const currencyFragment = document.createDocumentFragment();
      if (currencies.length > 0) {
        currencies.forEach((code) => {
          const opt = document.createElement('option');
          opt.value = code;
          opt.textContent = code;
          if (this.current.currency && this.current.currency.toUpperCase() === code.toUpperCase()) {
            opt.selected = true;
          }
          currencyFragment.appendChild(opt);
        });
      }
      this.currencySelect.appendChild(currencyFragment);
      this.currencySelect.disabled = currencies.length <= 1;

      // Languages
      this.languageSelect.innerHTML = '';
      const languages = loc && Array.isArray(loc.languages) ? loc.languages : [];
      const languageFragment = document.createDocumentFragment();
      languages.forEach(({ code, label }) => {
        const opt = document.createElement('option');
        opt.value = code;
        opt.textContent = label || code;
        if (this.current.language && this.current.language.toLowerCase() === String(code).toLowerCase()) {
          opt.selected = true;
        }
        languageFragment.appendChild(opt);
      });
      this.languageSelect.appendChild(languageFragment);
    }

    onCountryChange(event) {
      const value = event.currentTarget.value;
      this.updateCurrencyAndLanguage(value);
      this.renderCountryFlag(value);
    }

    renderCountryFlag(countryValue) {
      if (!this.flagContainer) return;
      const localeEntry = this.findLocaleByCountry(countryValue);
      if (!localeEntry) {
        this.flagContainer.innerHTML = '';
        return;
      }
      // Try mapping by name -> ISO via JSON map
      let isoLower = '';
      const nameMapScript = this.querySelector('script.country-name-map[type="application/json"]');
      if (nameMapScript) {
        try {
          const map = JSON.parse(nameMapScript.textContent || '[]');
          const match = map.find((m) => String(m.name).toLowerCase() === String(localeEntry.name).toLowerCase());
          if (match && match.iso) isoLower = String(match.iso).toLowerCase();
        } catch (_) {}
      }
      if (!isoLower) {
        isoLower = String(localeEntry.marketLocale || localeEntry.code || '').toLowerCase();
      }
      if (!isoLower) {
        this.flagContainer.innerHTML = '';
        return;
      }
      const tpl = this.querySelector(`template[data-flag-template="${isoLower}"]`);
      if (tpl && 'content' in tpl) {
        this.flagContainer.innerHTML = '';
        this.flagContainer.appendChild(tpl.content.cloneNode(true));
      } else {
        this.flagContainer.innerHTML = '';
      }
    }

    onApply() {
      const target = this.buildTargetUrl();
      if (target) {
        console.log('target', target);
        window.location.href = target;
      }
    }
  }

  customElements.define('locale-selects', LocaleSelects);
}


